import { useEffect, useMemo, useRef, useState } from "react";
import { CheckoutProgress } from "../../utils/interfaces";
import {
  createCheckoutProgressFromStep,
  getCheckoutProgressRank,
  getCheckoutProgressTransitionQueue,
  mergeCheckoutProgress,
} from "../../utils/checkoutProgress";

type Params = {
  pedidoId: string | number;
  method: CheckoutProgress["method"];
  remoteProgress?: CheckoutProgress;
  localProgress?: CheckoutProgress | null;
  idleProgress?: CheckoutProgress;
  enabled?: boolean;
  onCompleted?: (progress: CheckoutProgress) => void;
};

const STEP_DELAY_MS = 320;
const COMPLETED_HOLD_MS = 420;

export function useVisualCheckoutProgress({
  pedidoId,
  method,
  remoteProgress,
  localProgress,
  idleProgress,
  enabled = true,
  onCompleted,
}: Params) {
  const baseProgress = useMemo(() => {
    if (!enabled) {
      return idleProgress ?? mergeCheckoutProgress(remoteProgress, localProgress);
    }

    return mergeCheckoutProgress(remoteProgress, localProgress);
  }, [enabled, idleProgress, localProgress, remoteProgress]);

  const [displayedProgress, setDisplayedProgress] = useState<CheckoutProgress | undefined>(baseProgress);
  const displayedRef = useRef<CheckoutProgress | undefined>(baseProgress);
  const timersRef = useRef<number[]>([]);
  const handledCompletedKeyRef = useRef<string | null>(null);

  const clearTimers = () => {
    timersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    timersRef.current = [];
  };

  useEffect(() => {
    displayedRef.current = displayedProgress;
  }, [displayedProgress]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  useEffect(() => {
    if (!baseProgress) {
      return;
    }

    if (!enabled) {
      clearTimers();
      handledCompletedKeyRef.current = null;
      setDisplayedProgress(baseProgress);
      return;
    }

    if (baseProgress.status === "failed") {
      clearTimers();
      handledCompletedKeyRef.current = null;
      setDisplayedProgress(baseProgress);
      return;
    }

    const currentDisplayed = displayedRef.current;

    if (!currentDisplayed) {
      setDisplayedProgress(baseProgress);
      if (baseProgress.status !== "completed") {
        handledCompletedKeyRef.current = null;
      }
      return;
    }

    const currentRank = getCheckoutProgressRank(currentDisplayed.step);
    const targetRank = getCheckoutProgressRank(baseProgress.step);

    if (targetRank < currentRank) {
      if (targetRank === currentRank) {
        setDisplayedProgress(baseProgress);
      }
      return;
    }

    if (targetRank === currentRank) {
      setDisplayedProgress(baseProgress);

      if (baseProgress.status !== "completed") {
        handledCompletedKeyRef.current = null;
      } else {
        scheduleCompletedCallback(baseProgress);
      }

      return;
    }

    clearTimers();

    const transitionSteps = getCheckoutProgressTransitionQueue(method, currentDisplayed.step, baseProgress.step);

    if (transitionSteps.length === 0) {
      setDisplayedProgress(baseProgress);

      if (baseProgress.status === "completed") {
        scheduleCompletedCallback(baseProgress);
      } else {
        handledCompletedKeyRef.current = null;
      }

      return;
    }

    transitionSteps.forEach((step, index) => {
      const isLast = index === transitionSteps.length - 1;
      const timerId = window.setTimeout(() => {
        const nextProgress =
          isLast && step === baseProgress.step
            ? baseProgress
            : createCheckoutProgressFromStep(pedidoId, method, step as Exclude<typeof step, "failed">);

        setDisplayedProgress(nextProgress);

        if (isLast) {
          if (baseProgress.status === "completed") {
            scheduleCompletedCallback(baseProgress);
          } else {
            handledCompletedKeyRef.current = null;
          }
        }
      }, STEP_DELAY_MS * (index + 1));

      timersRef.current.push(timerId);
    });
  }, [baseProgress, enabled, method, onCompleted, pedidoId]);

  function scheduleCompletedCallback(progress: CheckoutProgress) {
    const completionKey = `${progress.pedido_id}:${progress.step}:${progress.updated_at}:${progress.redirect_to}`;

    if (handledCompletedKeyRef.current === completionKey) {
      return;
    }

    handledCompletedKeyRef.current = completionKey;

    const timerId = window.setTimeout(() => {
      onCompleted?.(progress);
    }, COMPLETED_HOLD_MS);

    timersRef.current.push(timerId);
  }

  return displayedProgress;
}
